import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import type { RootState } from "../../../redux/store";
import InvoiceModal from "./InvoiceModal/InvoiceModal";
import { useGetBillingDataQuery, useGetBillingDataUpcomingQuery, useGetOrganisationScreensQuery } from "../../../services";
import { BillingRow } from "../../../types";
import { selectOrganisationId } from "../../../redux/organisationReducer";
import { useGetSubscriptionsSummaryQuery } from "../../../services";
import { selectSubscriptionSummary } from "../../../redux/licenseReducer";

const Billing: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Infinite scroll state
  const [page, setPage] = useState(1);
  const [allBillingData, setAllBillingData] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const organisationId = useAppSelector(selectOrganisationId);

  const { data, isError, isFetching, isLoading: isQueryLoading } = useGetBillingDataQuery({
    page,
    limit: 10,
    organisation: organisationId,
  });

  const { data: upcomingData, isError: isUpcomingError, isFetching: isUpcomingFetching, isLoading: isUpcomingLoading } = useGetBillingDataUpcomingQuery({
    organisation: organisationId,
  });

  const subscriptionSummary = useAppSelector(selectSubscriptionSummary);



  // Instantly clear stale data when organisation switches
  React.useEffect(() => {
    setPage(1);
    setAllBillingData([]);
    setHasMore(true);
  }, [organisationId]);

  React.useEffect(() => {
    if (data && !isFetching) {
      const newResults = data.results || (Array.isArray(data) ? data : [data]);
      if (Array.isArray(newResults)) {
        if (page === 1) {
          setAllBillingData(newResults);
        } else {
          setAllBillingData((prev) => {
            const existingIds = new Set(prev.map((item) => item._id));
            const uniqueNewResults = newResults.filter((item) => !existingIds.has(item._id));
            return [...prev, ...uniqueNewResults];
          });
        }
        setHasMore(newResults.length === 10);
      } else {
        setHasMore(false);
      }
    }
  }, [data, page, isFetching]);

  // Observer for infinite scroll
  const loaderRef = React.useRef<HTMLTableRowElement | null>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isFetching, allBillingData.length]);

  // Helper to get product stats (summing quantities and prices)
  const getProductStats = (products: any[], type: "PREMIUM" | "BASIC", variant: "LICENSE" | "ADDON_SCREEN") => {
    let filtered = (products || []).filter((p: any) => p.type === type && p.variant === variant);

    if (filtered.length === 0) return null;
    return filtered.reduce(
      (acc, p) => ({
        quantity: acc.quantity + (p.quantity || 0),
        price: p.price,
        totalPrice: acc.totalPrice + (p.totalPrice || 0),
      }),
      { quantity: 0, price: 0, totalPrice: 0 }
    );
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);

    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  };

  // Helper to map API item to BillingRow
  const mapToBillingRow = (item: any): BillingRow => {
    const products = item.products || [];

    return {
      id: item._id || Math.random().toString(),

      // ✅ FIXED
      billingDate: formatDate(item.createdAt),

      // ✅ FIXED (NO +28 days)
      endDate: formatDate(item.endDate || item.subscription?.endDate),

      invoiceId: item.code || "--",
      description: item.products?.[0]?.name || "Subscription Plan",

      premium:
        getProductStats(products, "PREMIUM", "LICENSE") || {
          quantity: 0,
          price: 0,
          totalPrice: 0,
        },

      normal:
        getProductStats(products, "BASIC", "LICENSE") || {
          quantity: 0,
          price: 0,
          totalPrice: 0,
        },

      premiumAddons:
        getProductStats(products, "PREMIUM", "ADDON_SCREEN") || {
          quantity: 0,
          price: 0,
          totalPrice: 0,
        },

      basicAddons:
        getProductStats(products, "BASIC", "ADDON_SCREEN") || {
          quantity: 0,
          price: 0,
          totalPrice: 0,
        },

      status: item.status || item.invoiceStatus || "Upcoming",
      total: item.totalAmount || 0,
      currency: item.currency || "USD",

      code: item.code,
      invoiceUrl: item.invoiceUrl,

      isCouponApplied: item.isCouponApplied,
      coupon: item.coupon,
      couponDiscountAmount: item.couponDiscountAmount,
      couponDiscountPercentage: item.couponDiscountPercentage,

      specialDiscountAmount: item.specialDiscountAmount,
      specialDiscountPercentage: item.specialDiscountPercentage,

      taxAmount: item.taxAmount,
      taxPercentage: item.taxPercentage,

      totalAmount: item.totalAmount,
      totalCapturedAmount: item.totalCapturedAmount,
      totalDueAmount: item.totalDueAmount,
      totalReductionAmount: item.totalReductionAmount,
      totalProductAmount: item.totalProductAmount,
      gateway: item.gateway,
    };
  };

  // Map accumulated API response to BillingRow[]
  const billingData: BillingRow[] = React.useMemo(() => {
    const historicalRows = allBillingData.map(mapToBillingRow);

    let upcomingRows: BillingRow[] = [];
    if (upcomingData) {
      const rawUpcoming = upcomingData.results || (upcomingData.result ? [upcomingData.result] : (Array.isArray(upcomingData) ? upcomingData : [upcomingData]));
      if (Array.isArray(rawUpcoming)) {
        upcomingRows = rawUpcoming.map(item => ({
          ...mapToBillingRow(item),
          status: "Upcoming" // Force status for upcoming data
        }));
      }
    }

    return [...upcomingRows, ...historicalRows];
  }, [allBillingData, upcomingData]);


  const pageBg = theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50";
  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const thClass =
    theme === "dark"
      ? "px-4 py-3 text-left text-sm font-bold text-gray-300 border-b border-[#2C2C2C]"
      : "px-4 py-3 text-left text-sm font-bold text-gray-700 border-b border-gray-200";

  const tdClass =
    theme === "dark"
      ? "px-4 py-3 text-sm text-text-table border-b border-[#2C2C2C]"
      : "px-4 py-3 text-sm text-text-table border-b border-gray-200";

  const headerStickyClasses =
    theme === "dark"
      ? "sticky top-0 z-10 bg-[#1E1E1E]"
      : "sticky top-0 z-10 bg-white";

  // sidebar width handling (64px collapsed, 256px expanded)
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // Modal state
  const [openInvoice, setOpenInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<BillingRow | null>(
    null
  );

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Scoped scrollbar styles */}
      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;                /* Firefox */
          scrollbar-color: #00000038 transparent;    /* Firefox */
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #000;               /* Black thumb */
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #000;
        }
      `}</style>

      {/* Breadcrumb + Title */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">
            Subscription Overview
          </h1>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
                stroke="#ADADAD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
                stroke="#ADADAD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Link
              to="/a"
              className={theme === "dark" ? "text-gray-400" : "text-gray-500"}
            >
              Dashboard
            </Link>
            <span
              className={theme === "dark" ? "text-gray-600" : "text-gray-400"}
            >
              /
            </span>
            <span className="text-[#B6CB24] font-medium">Billing</span>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className={`${cardClass} rounded-lg overflow-hidden`}>
        <div
          className={
            theme === "dark"
              ? "px-4 py-3 border-b border-[#2C2C2C] text-gray-200 flex items-center justify-between"
              : "px-4 py-3 border-b border-gray-200 text-gray-800 flex items-center justify-between"
          }
        >
          <span>Billing Details</span>

        </div>

        <div className="overflow-x-auto">
          <div
            className="max-h-[60vh] overflow-y-auto custom-scrollbar"
          >
            <table className="min-w-full border-separate border-spacing-0">
              <thead className={headerStickyClasses}>
                <tr className="bg-gray-640 text-gray-700">
                  <th className={thClass}>Billing Date</th>
                  <th className={thClass}>Ending Date</th>
                  <th className={thClass}>Description</th>
                  {/* <th className={`${thClass} flex items-center gap-2`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12.75"
                        cy="12.75"
                        r="10.75"
                        fill="url(#paint0_radial_278_5764)"
                      />
                      <g filter="url(#filter0_n_278_5764)">
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                          fill="url(#paint1_linear_278_5764)"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                          fill="#C6D92C"
                          fill-opacity="0.1"
                        />
                      </g>
                      <circle
                        cx="12.75"
                        cy="12.75"
                        r="10.25"
                        stroke="url(#paint2_linear_278_5764)"
                        stroke-opacity="0.88"
                      />
                      <path
                        d="M11.2939 7.81641C11.6993 7.06107 12.8114 7.06112 13.2168 7.81641L14.4531 10.1191L14.5117 10.2295L14.6357 10.249L17.3369 10.6572C18.1918 10.7865 18.5864 11.8269 17.9287 12.4531L16.0176 14.2734L15.9219 14.3652L15.9434 14.4951L16.3633 17.0254C16.4394 17.4839 16.238 17.8433 15.9238 18.0518C15.6023 18.2649 15.1713 18.3136 14.8096 18.1387L12.3643 16.9561L12.2559 16.9033L12.1465 16.9561L9.70117 18.1387C9.33945 18.3135 8.90938 18.2648 8.58789 18.0518C8.27342 17.8433 8.07137 17.4841 8.14746 17.0254L8.56738 14.4951L8.58887 14.3652L8.49316 14.2734L6.58203 12.4531C5.92445 11.8269 6.31904 10.7865 7.17383 10.6572L9.875 10.249L9.99902 10.2295L10.0586 10.1191L11.2939 7.81641Z"
                        stroke="url(#paint3_linear_278_5764)"
                        stroke-opacity="0.88"
                        stroke-width="0.5"
                      />
                      <defs>
                        <filter
                          id="filter0_n_278_5764"
                          x="2"
                          y="2"
                          width="21.5"
                          height="21.5"
                          filterUnits="userSpaceOnUse"
                          color-interpolation-filters="sRGB"
                        >
                          <feFlood
                            flood-opacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feTurbulence
                            type="fractalNoise"
                            baseFrequency="2 2"
                            stitchTiles="stitch"
                            numOctaves="3"
                            result="noise"
                            seed="2744"
                          />
                          <feColorMatrix
                            in="noise"
                            type="luminanceToAlpha"
                            result="alphaNoise"
                          />
                          <feComponentTransfer
                            in="alphaNoise"
                            result="coloredNoise1"
                          >
                            <feFuncA
                              type="discrete"
                              tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                            />
                          </feComponentTransfer>
                          <feComposite
                            operator="in"
                            in2="shape"
                            in="coloredNoise1"
                            result="noise1Clipped"
                          />
                          <feFlood
                            flood-color="rgba(0, 0, 0, 0.25)"
                            result="color1Flood"
                          />
                          <feComposite
                            operator="in"
                            in2="noise1Clipped"
                            in="color1Flood"
                            result="color1"
                          />
                          <feMerge result="effect1_noise_278_5764">
                            <feMergeNode in="shape" />
                            <feMergeNode in="color1" />
                          </feMerge>
                        </filter>
                        <radialGradient
                          id="paint0_radial_278_5764"
                          cx="0"
                          cy="0"
                          r="1"
                          gradientUnits="userSpaceOnUse"
                          gradientTransform="translate(10.5 10) rotate(54.7824) scale(10.4043)"
                        >
                          <stop stop-color="#D1E156" />
                          <stop offset="0.552885" stop-color="#C6D92C" />
                          <stop offset="1" stop-color="#7A8618" />
                        </radialGradient>
                        <linearGradient
                          id="paint1_linear_278_5764"
                          x1="12.75"
                          y1="3"
                          x2="12.75"
                          y2="22.5"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#2E2E2E" />
                          <stop offset="1" stop-color="#666666" />
                        </linearGradient>
                        <linearGradient
                          id="paint2_linear_278_5764"
                          x1="2"
                          y1="12.75"
                          x2="23.5"
                          y2="12.75"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#C6D92C" />
                          <stop offset="0.533654" stop-color="#EDF5C0" />
                          <stop offset="1" stop-color="#C6D92C" />
                        </linearGradient>
                        <linearGradient
                          id="paint3_linear_278_5764"
                          x1="6"
                          y1="12.7476"
                          x2="18.5109"
                          y2="12.7476"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#C6D92C" />
                          <stop offset="0.533654" stop-color="#EDF5C0" />
                          <stop offset="1" stop-color="#C6D92C" />
                        </linearGradient>
                      </defs>
                    </svg>
                    Premium Licenses
                  </th>
                  <th className={thClass}>Basic Licenses</th>
                  <th className={thClass}>Premium Addons</th>
                  <th className={thClass}>Basic Addons</th> */}
                  <th className={thClass}>Status</th>
                  <th className={thClass}>Total Amount</th>
                  <th className={thClass}></th>
                </tr>
              </thead>
              <tbody className="min-w-full">
                {isQueryLoading && page === 1 && (
                  <tr>
                    <td className={tdClass} colSpan={10}>
                      Loading...
                    </td>
                  </tr>
                )}
                {isError && (
                  <tr>
                    <td className={tdClass} colSpan={10}>
                      Error loading billing data.
                    </td>
                  </tr>
                )}
                {!isQueryLoading && !isError && billingData.length === 0 && (
                  <tr>
                    <td className={tdClass} colSpan={10}>
                      No billing data found.
                    </td>
                  </tr>
                )}
                {billingData.map((row) => (
                  <tr key={row.id}>
                    <td className={tdClass}>{row.billingDate}</td>
                    <td className={tdClass}>{row.endDate}</td>
                    <td className={tdClass}>
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M20.9999 10.5V10C20.9999 6.22876 20.9999 4.34315 19.8284 3.17157C18.6568 2 16.7712 2 12.9999 2H12C8.22883 2 6.34323 2 5.17166 3.17156C4.00009 4.34312 4.00007 6.22872 4.00004 9.99993L4 14.5C3.99997 17.7874 3.99996 19.4312 4.90788 20.5375C5.07412 20.7401 5.25986 20.9258 5.46242 21.0921C6.56877 22 8.21249 22 11.4999 22"
                            stroke="#F3F3F6"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M7.5 7H16.5"
                            stroke="#F3F3F6"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M7.5 12H13.5"
                            stroke="#F3F3F6"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M20.5 20V17C20.5 15.5706 19.1569 14 17.5 14C15.8431 14 14.5 15.5706 14.5 17V20.5C14.5 21.3284 15.1716 22 16 22C16.8284 22 17.5 21.3284 17.5 20.5V17"
                            stroke="#C6D92C"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        {row.description}
                      </div>
                    </td>
                    {/* <td className={tdClass}>
                      {row.premium ? row.premium.quantity : 0}
                    </td>
                    <td className={tdClass}>
                      {row.normal ? row.normal.quantity : 0}
                    </td>
                    <td className={tdClass}>
                      {row.premiumAddons ? row.premiumAddons.quantity : 0}
                    </td>
                    <td className={tdClass}>
                      {row.basicAddons ? row.basicAddons.quantity : 0}
                    </td> */}
                    <td className={tdClass}>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${String(row.status).toUpperCase() === "PAID"
                        ? "bg-green-100 text-green-700"
                        : String(row.status).toUpperCase() === "UPCOMING"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-yellow-100 text-yellow-700"
                        }`}>
                        {String(row.status).charAt(0).toUpperCase() + String(row.status).slice(1).toLowerCase()}
                      </span>
                    </td>
                    <td className={tdClass}>
                      {row.currency === "USD" ? "$" : row.currency === "AED" ? "د.إ" : row.currency === "INR" ? "₹" : row.currency} {row.total}
                    </td>
                    <td className={tdClass}>
                      <button
                        onClick={() => {
                          setSelectedInvoice(row);
                          setOpenInvoice(true);
                        }}
                        className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg border transition-colors duration-200
    ${theme === "dark"
                            ? "text-gray-100 bg-gray-900 border-gray-600 hover:bg-gray-800"
                            : "text-[#1A1A1A] bg-[#EDF5C0] border-[#B6CB24] "
                          }`}
                      >
                        View
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M9.00005 6L15 12L9 18"
                            stroke="#C6D92C"
                            stroke-width="1.5"
                            stroke-miterlimit="16"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
                {isFetching && page > 1 && (
                  <tr>
                    <td colSpan={10} className={`${tdClass} text-center py-4`}>
                      Loading more...
                    </td>
                  </tr>
                )}
                {/* Loader Ref trigger */}
                <tr ref={loaderRef}>
                  <td colSpan={10} style={{ height: '1px' }}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      {selectedInvoice && (
        <InvoiceModal
          open={openInvoice}
          data={selectedInvoice}
          onClose={() => setOpenInvoice(false)}
        />
      )}
    </div>
  );
};

export default Billing;
